import { useLightMode } from '@/hooks/useLightMode'
import { usePermissions } from '@/hooks/usePermissions'
import { useTranslation } from 'react-i18next'

import { Alert, Button, Box as DarkBox, Modal } from '@hivewatch/dark'

import { useDoorTailgateGridsEnabledQuery } from '@/graphql/generated/hooks'
import { DeviceType } from '@/graphql/generated/schemas'

import { AssociateDeviceI, ConnectionI } from '../../types/types'
import { isTailgatingEnabledForOtherDevices } from '../utils/utils'
import { darkAssociateModalStyles } from './DarkAssociateDeviceModal.styles'
import { DarkAssociationStatusPills } from './DarkAssociationStatusPills'
import { DarkEnableTailgateBox } from './DarkEnableTailgateBox'

export interface DarkAssociateDeviceModalIProps {
  associateDevice: AssociateDeviceI
  connection: ConnectionI
  loading: boolean
  isEnableTailgateLoading: boolean
  isOpen: boolean
  hideAssociateButton: boolean
  onClose: () => void
  onAssociate?: () => void
  onDisassociate: () => void
  onEnableTailgate: (isTailgateEnabled: boolean) => void
  onOpenGrid: () => void
  zIndex?: number
}

export const DarkAssociateDeviceModal = ({
  associateDevice,
  connection,
  loading,
  isEnableTailgateLoading,
  isOpen,
  hideAssociateButton,
  onClose,
  onAssociate,
  onDisassociate,
  onEnableTailgate,
  onOpenGrid,
  zIndex,
}: DarkAssociateDeviceModalIProps) => {
  const { t } = useTranslation()
  const colorScheme = useLightMode()
  const { deviceType, cameraName, doorName, doorId, cameraId } = associateDevice
  const { isConnected, isTailgateGridConfigured, isTailgateEnabled } =
    connection || {}

  const { data, loading: isTailgateGridsLoading } =
    useDoorTailgateGridsEnabledQuery({
      fetchPolicy: 'network-only',
      skip: !isConnected,
      variables: { id: doorId },
    })
  const hasExistingTailgateEnabled = isTailgatingEnabledForOtherDevices(
    data,
    cameraId
  )
  const { shouldEnableAssociateDevices, shouldEnableConfigureTailgateGrid } =
    usePermissions()

  const shouldDisableTailgate =
    !isConnected || // The devices are not associated
    !isTailgateGridConfigured || // The grid is not configured
    (hasExistingTailgateEnabled && !isTailgateEnabled) // tailgate is enabled already on a different device + not enabled for this device (This allows use to disable it but not enable)

  const isConfigureGridsDisabled =
    !isConnected || // The devices are not associated
    !shouldEnableConfigureTailgateGrid || // has permission
    loading ||
    isEnableTailgateLoading

  // Only Doors & Cameras can configure grids. This will have issues in future if we allow something like Doors &
  // Alarms to be connected, but for now it's safe to assume that if we've gotten this far we should show this button.
  const canConfigureGrids =
    deviceType === DeviceType.Door || deviceType === DeviceType.Camera

  const s = darkAssociateModalStyles(colorScheme)

  return (
    <Modal
      colorScheme={colorScheme}
      footer={
        // Rendered as one footer node: dark Modal only renders its own
        // Cancel/Save pair when a save handler is present, and this modal's
        // primary actions are the association + grids buttons.
        <DarkBox {...s.footerRow}>
          <Button
            colorScheme={colorScheme}
            onClick={onClose}
            variant='tertiary'
          >
            {t('common:cancel')}
          </Button>
          <DarkBox {...s.footerActions}>
            {!hideAssociateButton && (
              <Button
                colorScheme={colorScheme}
                data-testid='DarkAssociateDeviceModal:associateButton'
                disabled={
                  !shouldEnableAssociateDevices || isEnableTailgateLoading
                }
                isLoading={loading}
                onClick={() => {
                  isConnected ? onDisassociate() : onAssociate?.()
                }}
                variant={isConnected ? 'danger' : 'primary'}
              >
                {isConnected
                  ? t('floors:tailgate_disassociate_devices')
                  : t('floors:tailgate_associate_devices')}
              </Button>
            )}
            {canConfigureGrids && (
              <Button
                colorScheme={colorScheme}
                data-testid='DarkAssociateDeviceModal:gridsButton'
                disabled={isConfigureGridsDisabled}
                onClick={onOpenGrid}
                variant='primary'
              >
                {isTailgateGridConfigured
                  ? t('floors:tailgate_edit_camera_grids')
                  : t('floors:tailgate_configure_camera_grids')}
              </Button>
            )}
          </DarkBox>
        </DarkBox>
      }
      isOpen={isOpen}
      isSaveHidden
      onClose={onClose}
      title={t('floors:tailgate_edit_device_association')}
      zIndex={zIndex}
    >
      <DarkBox {...s.body} data-testid='DarkAssociateDeviceModal:body'>
        <DarkBox>
          <DarkBox {...s.sectionLabel} pb='8px'>
            {t('floors:tailgate_camera_label')}
          </DarkBox>
          <DarkBox {...s.fieldValue}>{cameraName}</DarkBox>
        </DarkBox>
        <DarkBox>
          <DarkBox {...s.sectionLabel} pb='8px'>
            {t('floors:tailgate_device_label')}
          </DarkBox>
          <DarkBox {...s.fieldValue}>{doorName}</DarkBox>
        </DarkBox>
        {isConnected && (
          <DarkBox>
            <DarkBox {...s.sectionLabel} pb='8px'>
              {t('floors:tailgate_status_label')}
            </DarkBox>
            <DarkAssociationStatusPills
              colorScheme={colorScheme}
              connection={connection}
            />
          </DarkBox>
        )}
        <DarkBox>
          <DarkBox {...s.sectionLabel} pb='8px'>
            {t('floors:tailgate_features_label')}
          </DarkBox>
          <DarkEnableTailgateBox
            colorScheme={colorScheme}
            isDisabled={shouldDisableTailgate}
            isEnabled={isTailgateEnabled}
            isLoading={isEnableTailgateLoading || isTailgateGridsLoading}
            onToggleChange={onEnableTailgate}
          />
        </DarkBox>
        <DarkBox {...s.warningList}>
          {!hideAssociateButton && isTailgateGridConfigured && (
            <Alert
              colorScheme={colorScheme}
              description={t('floors:tailgate_disassociate_warning')}
              size='compact'
              variant='warning'
            />
          )}
          {hasExistingTailgateEnabled && (
            <Alert
              colorScheme={colorScheme}
              description={t('floors:tailgate_already_enabled_warning')}
              size='compact'
              variant='warning'
            />
          )}
        </DarkBox>
      </DarkBox>
    </Modal>
  )
}
